import { Mail, Phone } from 'lucide-react'
import { useState } from 'react'

import { CopyButton } from '@/components/ui/CopyButton'
import { type InsuranceCarrier } from '@/models/insuranceCarrier'
import { type SalesLead } from '@/models/salesLead'
import { ActivityItem } from './ActivityItem'
import { CarrierComposer } from './CarrierComposer'

interface LeadCarrierCardProps {
  lead: SalesLead
  carrier: InsuranceCarrier
}

export function LeadCarrierCard({ lead, carrier }: LeadCarrierCardProps) {
  const [replyToActivityId, setReplyToActivityId] = useState<string | null>(null)
  const activities = lead.carrierActivities.filter((a) => a.insuranceCarrier === carrier.id)
  const channel = lead.carrierChannels.find((c) => c.carrier === carrier.id)

  return (
    <div className="flex flex-col gap-3">
      {channel && (
        <div className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm">
          <Mail className="size-3.5 shrink-0" />
          <span>CC or forward {carrier.name} email to</span>
          <span className="text-foreground font-medium">{channel.email}</span>
          <CopyButton value={channel.email} />
        </div>
      )}

      {carrier.contacts.length === 0 ? (
        <p className="text-muted-foreground text-sm">No contacts on this carrier yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {carrier.contacts.map((contact) => (
            <li key={contact.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-medium">{contact.name}</span>
              {contact.title && <span className="text-muted-foreground">{contact.title}</span>}
              {contact.email && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <Mail className="size-3.5" />
                  {contact.email}
                </span>
              )}
              {contact.phone && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <Phone className="size-3.5" />
                  {contact.phone}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <CarrierComposer lead={lead} carrier={carrier} />

      {activities.length > 0 && (
        <div className="flex flex-col gap-3">
          {activities.map((activity) => (
            <ActivityItem
              key={activity.id}
              lead={lead}
              activity={activity}
              carrier={carrier}
              isReplying={replyToActivityId === activity.id}
              onReply={setReplyToActivityId}
              onCancelReply={() => setReplyToActivityId(null)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
